import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetAudioList, addTrack, setAutoPlay } from '../../redux/slices/musicPlayerSlice';
import { setListContext, clearListContext } from '../../redux/slices/listContextSlice';
import { useToast } from '../../contexts/ToastContext';
import { silentUrlFor } from '../../utils/resolvePlaybackSrc';
import { resolveMixLinkLazy } from '../../utils/lazyMixResolveCache';
import { isNative } from '../../utils/webOnly';
import { entityId } from '../../utils/entityId';
import { track as trackEvent } from '../../utils/analytics';

/**
 * Delayed-click handler: waits 250ms before firing action.
 * Cancelled by double-click, text selection, or mouse drag (>5px).
 */
let _clickTimer = null;
let _mouseDownPos = null;
export const delayedClick = (action) => ({
    onMouseDown: (e) => {
        _mouseDownPos = { x: e.clientX, y: e.clientY };
    },
    onClick: (e) => {
        if (window.getSelection()?.toString()) return;
        if (_mouseDownPos) {
            const dx = e.clientX - _mouseDownPos.x;
            const dy = e.clientY - _mouseDownPos.y;
            if (dx * dx + dy * dy > 25) return; // dragged more than 5px
        }
        clearTimeout(_clickTimer);
        _clickTimer = setTimeout(() => { _clickTimer = null; action(); }, 250);
    },
    onDoubleClick: () => {
        clearTimeout(_clickTimer);
        _clickTimer = null;
    },
});

// Cancel any pending row-click action. Called from in-row controls (⋯
// menu, like button, etc.) on pointer/mouse down so a row tap that
// was already queued for 250ms doesn't fire AFTER the user has moved
// on to a per-row button — that's what was making the ⋯ menu appear
// to "not respond": it opened, then the queued row navigation tore
// down the menu's host component before the user noticed it.
export const cancelDelayedClick = () => {
    clearTimeout(_clickTimer);
    _clickTimer = null;
};

/**
 * Detect the streaming platform from a URL.
 */
export const detectPlatform = (url) => {
    if (!url) return null;
    const lower = url.toLowerCase();
    if (lower.includes('soundcloud.com')) return 'soundcloud';
    if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'youtube';
    if (lower.includes('mixcloud.com')) return 'mixcloud';
    return null;
};

/**
 * Check if a URL is a supported mix platform.
 */
export const isSupportedMixUrl = (url) => detectPlatform(url) !== null;

// ── Lazy first-fill scaffolding ────────────────────────────────────────────
//
// LAZY_MIX_RESOLVE_MODE controls whether we offer a play button on mixes
// with no link. The flag's value is owned BY THE SERVER (env var on
// music-info-server). Renderer polls `djpal:refreshLazyConfig` at app
// start + every LAZY_CONFIG_POLL_MS so server-side flag flips propagate
// to running clients without an app update.
//
// Default DISABLED → canPlayMix is identical to the historical
// `isSupportedMixUrl(mix.link)` check until the first successful poll
// upgrades the cache.
const LAZY_CONFIG_POLL_MS = 5 * 60 * 1000;
let _lazyMixResolveMode = 'DISABLED';
let _lazyMixResolveModeLoaded = false;

// canPlayMix() and friends read `_lazyMixResolveMode` synchronously, but
// the value is updated asynchronously (server poll). React has no way to
// know the module-level variable changed → mix-render surfaces show the
// stale DISABLED state until something else triggers a re-render.
// Components subscribe via `useLazyMixResolveMode()` below; when the
// value flips, we notify them so their `canPlayMix()` calls re-run.
const _modeSubscribers = new Set();
function _notifyModeChange() {
    for (const fn of _modeSubscribers) {
        try { fn(); } catch { /* one subscriber crashing must not break the rest */ }
    }
}

async function _pollLazyConfigOnce() {
    if (typeof window === 'undefined' || !window.electron?.djpal?.refreshLazyConfig) return;
    try {
        const token = (() => {
            try { return localStorage.getItem('djpal:token') || null; } catch { return null; }
        })();
        // Refresh the main-process cache (which is the authority for IPC
        // handlers), then read the mix-resolve value via the existing
        // getter so this module's local mirror stays in sync.
        await window.electron.djpal.refreshLazyConfig(token);
        const r = await window.electron.djpal.getLazyMixResolveMode();
        const next = (r && r.resolveMode) || 'DISABLED';
        const changed = next !== _lazyMixResolveMode;
        _lazyMixResolveMode = next;
        if (changed) _notifyModeChange();
    } catch { /* leave previous value; next poll will retry */ }
    finally { _lazyMixResolveModeLoaded = true; }
}
// Kick off immediately. Then re-poll whenever the user comes back to
// the app (visibilitychange → visible) with a cooldown matching the
// previous setInterval cadence. This preserves the "flag flips
// propagate within ~5min of refocus" guarantee while removing a
// constantly-firing setInterval that contributed to background timer
// churn (and to the burst of work on every macOS refocus before
// background-throttling was disabled — see main.js).
_pollLazyConfigOnce();
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    let _lazyConfigLastPollAt = Date.now();
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        const now = Date.now();
        if (now - _lazyConfigLastPollAt < LAZY_CONFIG_POLL_MS) return;
        _lazyConfigLastPollAt = now;
        _pollLazyConfigOnce();
    });
}

// Subscribe a component to mode changes. Call this once at the top of
// any component that uses `canPlayMix()` to render mix UI; the return
// value isn't used — the call itself forces a re-render when the mode
// flips so `canPlayMix()` is re-evaluated with the new value.
export function useLazyMixResolveMode() {
    const [, setTick] = useState(0);
    useEffect(() => {
        const fn = () => setTick(n => n + 1);
        _modeSubscribers.add(fn);
        return () => _modeSubscribers.delete(fn);
    }, []);
    return _lazyMixResolveMode;
}

// Test whether a mix has the minimum metadata needed to do a meaningful
// YouTube search. Matches the gates inside the lazy-resolve IPC handler.
function _mixSearchableMetadata(mix) {
    if (!mix) return false;
    const artist = (mix.artistsname || mix.artist || '').toString().trim();
    const event = (mix.event_name || mix.eventName || '').toString().trim();
    return !!(artist && event);
}

// Single source of truth for "should this mix surface render a play
// button?". Layered: keep the legacy link-based path AND opt-in to the
// lazy first-fill path when the flag is on. With LAZY_MIX_RESOLVE_MODE
// at its DISABLED default, returns the same boolean as
// `isSupportedMixUrl(mix.link)` — UI looks identical to today.
export const canPlayMix = (mix) => {
    if (!mix) return false;
    // NATIVE (iOS + Android): mixes are never playable and we surface no
    // desktop-playback upsell. This hides every mix play icon and suppresses
    // the "play on desktop" messaging at all call sites that gate on
    // canPlayMix. Web and Electron desktop are unaffected. (Android matches
    // iOS for store-compliance — full mix streaming isn't allowed in either
    // store's app; see Option A.)
    if (isNative) return false;
    // Manual takedown — refuse playback regardless of link / lazy mode.
    if (mix.playbackBlocked === true) return false;
    if (mix.link && isSupportedMixUrl(mix.link)) return true;
    if (_lazyMixResolveMode !== 'DISABLED' && _mixSearchableMetadata(mix)) return true;
    return false;
};

export const isLazyMixResolveEnabled = () => _lazyMixResolveMode !== 'DISABLED';
export const isLazyMixResolveModeLoaded = () => _lazyMixResolveModeLoaded;

// Module-level so all hook instances share the same active mix ID
let _activeMixId = null;

// Stable key for the player's internal "which mix is loading/playing"
// tracking (loadingMixId / playingMixId). entityId(mix) is the real catalog
// id when the mix has one; the link/title fallbacks exist ONLY so ephemeral
// UI state works for non-catalog mixes. Callers must NEVER write this key
// into `mix.id` — the old withMixId/`liked-mix-<title>` fabrications leaked
// into the queue entry's mixId and broke id-based navigation (the player bar
// sent a URL/title as public_id and landed on an empty page).
export const mixTrackingKey = (mix) => {
    if (!mix) return null;
    return entityId(mix) || mix.link || (mix.title ? `mix-title:${mix.title}` : null);
};

/**
 * Hook to handle mix playback via yt-dlp.
 * Returns { playMix, loadingMixId, playingMixId } for use in components.
 */
export const useMixPlayback = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [loadingMixId, setLoadingMixId] = useState(null);
    const autoPlay = useSelector((state) => state.musicPlayer.autoPlay);
    const audioList = useSelector((state) => state.musicPlayer.audioList);
    const loadingRef = useRef(false);
    const [, forceUpdate] = useState(0);

    // Clear active mix when a track preview starts playing
    useEffect(() => {
        const handleMixStop = () => {
            if (_activeMixId) {
                _activeMixId = null;
                forceUpdate(n => n + 1);
            }
        };
        window.addEventListener('djpal-mix-stop', handleMixStop);
        return () => window.removeEventListener('djpal-mix-stop', handleMixStop);
    }, []);

    // Clear active mix if audioList changed to a non-mix track
    useEffect(() => {
        if (_activeMixId && audioList.length > 0) {
            const current = audioList[audioList.length - 1];
            if (!current.isMix) {
                _activeMixId = null;
                forceUpdate(n => n + 1);
            }
        }
    }, [audioList]);

    // The mix is "playing" only if it's the active mix AND the player is playing
    const playingMixId = autoPlay ? _activeMixId : null;

    const playMix = useCallback(async (mix, playContext = null) => {
        const trackKey = mixTrackingKey(mix);
        // Manual takedown — refuse to extract a stream. Mirrors canPlayMix so
        // even if a caller bypasses the disabled play button, the IPC
        // extractor is never invoked for this mix.
        if (mix?.playbackBlocked === true) {
            toast.error('Playback unavailable for this mix');
            return;
        }
        if (!mix?.link) {
            // Lazy first-fill: when LAZY_MIX_RESOLVE_MODE is on, a linkless
            // mix is still playable — we search YouTube on click and play
            // the result. The flag is DISABLED by default; behavior matches
            // the historical "no link → toast + bail" until we flip it.
            if (isLazyMixResolveEnabled() && _mixSearchableMetadata(mix)) {
                if (loadingRef.current) return;
                loadingRef.current = true;
                setLoadingMixId(trackKey);
                try {
                    const recovery = await resolveMixLinkLazy(mix, { timeoutMs: 12000 });
                    if (recovery?.link) {
                        const filledMix = { ...mix, link: recovery.link };
                        loadingRef.current = false;
                        setLoadingMixId(null);
                        return playMix(filledMix, playContext);
                    }
                    toast.error('Could not find a playable source for this mix');
                } catch (err) {
                    console.warn('[MixPlayback] lazy first-fill failed', err);
                    toast.error('Could not find a playable source for this mix');
                } finally {
                    loadingRef.current = false;
                    setLoadingMixId(null);
                }
                return;
            }
            toast.error('No audio source available for this mix');
            return;
        }

        // If this mix is the active one, toggle pause/play
        if (_activeMixId === trackKey && window.musicPlayerInstance) {
            const audio = window.musicPlayerInstance;
            if (audio.paused) {
                audio.play?.();
            } else {
                audio.pause?.();
            }
            return;
        }

        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoadingMixId(trackKey);
        // Spin the bottom-player play button while extracting (matches DJPalAudioPreview)
        window.dispatchEvent(new CustomEvent('djpal-track-extract-start'));

        try {
            let effectiveLink = mix.link;
            let result = await window.electron.djpal.extractStreamUrl(effectiveLink);

            // Recovery path: if extraction failed, ask main process whether
            // the link is REALLY dead and (if so) try to find a YouTube
            // replacement. The main process gates this on
            // LAZY_MIX_REPLACE_MODE and bails on rate-limit signals. On
            // success we transparently retry the extract with the new link.
            if (result.error) {
                console.warn('[MixPlayback] extract failed, attempting lazy recovery', { mixId: mix.id, error: result.error });
                const recovery = await resolveMixLinkLazy(mix, {
                    replaceLink: mix.link,
                    extractError: result.error,
                    timeoutMs: 12000,
                });
                if (recovery?.link && recovery.link !== mix.link) {
                    effectiveLink = recovery.link;
                    result = await window.electron.djpal.extractStreamUrl(effectiveLink);
                }
            }

            if (result.error) {
                console.error('[MixPlayback] Error:', result.error);
                toast.error('Unable to load mix. The audio source may be unavailable or restricted.');
                loadingRef.current = false;
                setLoadingMixId(null);
                return;
            }

            if (!result.streamUrl || result.streamUrl === '' || result.streamUrl === 'undefined') {
                console.error('[MixPlayback] Invalid stream URL:', result.streamUrl);
                toast.error('No playable audio stream found for this mix');
                loadingRef.current = false;
                setLoadingMixId(null);
                return;
            }

            // Validate the URL is actually a valid URL
            try {
                const urlObj = new URL(result.streamUrl);
                // Check if it's a DASH manifest which isn't supported by HTML5 audio
                if (result.streamUrl.includes('.mpd') || result.streamUrl.includes('manifest.mpd')) {
                    console.error('[MixPlayback] DASH manifest detected (unsupported):', result.streamUrl);
                    toast.error('This mix format is not supported. Try another mix.');
                    loadingRef.current = false;
                    setLoadingMixId(null);
                    return;
                }
            } catch (urlError) {
                console.error('[MixPlayback] Invalid URL format:', result.streamUrl);
                toast.error('Invalid audio source URL for this mix');
                loadingRef.current = false;
                setLoadingMixId(null);
                return;
            }

            const cover = result.thumbnail || mix.artwork || mix.artworkUrl || mix.image || '';
            // Stop any playing track previews
            window.dispatchEvent(new CustomEvent('djpal-track-stop-all'));

            dispatch(resetAudioList());
            const name = mix.date
                ? `${mix.title || 'Untitled Mix'} (${mix.date})`
                : (mix.title || 'Untitled Mix');

            // HLS routes audio.src to a silent placeholder; hls.js takes over
            // via hlsSrc. See DJPalAudioPreview for the rationale.
            const isHls = !!result.isHls;
            const musicSrc = isHls ? silentUrlFor(result.streamUrl) : result.streamUrl;
            dispatch(addTrack({
                name,
                singer: mix.djName || mix.artist || '',
                cover,
                musicSrc,
                // effectiveLink not mix.link — after a lazy-replacement recovery
                // this is the new YouTube URL; using the original (dead) link
                // would mis-key the stream cache + any future re-extract.
                originalUrl: effectiveLink,
                isMix: true,
                isHls,
                ...(isHls ? { hlsSrc: result.streamUrl } : {}),
                mixId: entityId(mix),
                mixTitle: mix.title || '',
            }));
            dispatch(setAutoPlay(true));
            trackEvent('mix_play', {
                mix_id: entityId(mix) || undefined,
                artist: mix.djName || mix.artist || '',
                lazy_recovered: effectiveLink !== mix.link || undefined,
            });
            _activeMixId = trackKey;
            if (playContext) {
                dispatch(setListContext({ items: playContext.items, currentIndex: playContext.index, type: playContext.type || 'mixes' }));
            } else {
                dispatch(clearListContext());
            }

            // Wait for the player to pick up the new track, then play
            // HLS tracks are played by hls.js — skip manual play() to avoid false errors
            if (!result.isHls) {
                setTimeout(() => {
                    if (window.musicPlayerInstance) {
                        const playPromise = window.musicPlayerInstance.play?.();
                        if (playPromise && typeof playPromise.catch === 'function') {
                            playPromise.catch(err => {
                                // Post-`extractStreamUrl` audio-element rejection — this
                                // means jinke got a stream URL but the browser couldn't
                                // decode/play it (codec issue, network blip, expired CDN
                                // URL). The bogus-stream-URL case is caught upstream
                                // (extractStreamUrl returns an error and we route through
                                // recovery), so "try again" is genuinely an option here.
                                console.warn('[MixPlayback] Play failed:', err);
                                toast.error('Couldn’t play this mix. Try again, or pick a different one.');
                                _activeMixId = null;
                                forceUpdate(n => n + 1);
                            });
                        }
                    }
                }, 500);
            }

            // Safety timeout: if nothing plays within 10s, show error (skip for HLS — hls.js handles it)
            if (!result.isHls) {
                setTimeout(() => {
                    const audio = window.musicPlayerInstance;
                    // "Stuck" = never produced ANY playback. The old
                    // `paused || readyState < 2` check fired when the USER
                    // paused within the first 10s — a false "failed to load"
                    // toast on a mix that was audibly playing.
                    const neverPlayed = audio && audio.currentTime === 0 && audio.readyState < 2;
                    if (_activeMixId === trackKey && neverPlayed) {
                        console.warn('[MixPlayback] Audio stuck — not playing after 10s');
                        toast.error(typeof navigator !== 'undefined' && navigator.onLine === false
                            ? 'Mix failed to load — you’re offline. Reconnect and try again.'
                            : 'Mix failed to load. The audio source may be unavailable.');
                        _activeMixId = null;
                        forceUpdate(n => n + 1);
                        // Emptying the list is what clears jinke's internal
                        // `loading` flag (changeAudioLists → resetAudioStatus).
                        // Without it the play button spins forever on the dead
                        // source AND refuses to re-play the same entry.
                        dispatch(setAutoPlay(false));
                        dispatch(resetAudioList());
                    }
                }, 10000);
            }
        } catch (err) {
            console.error('[MixPlayback] Error:', err);
            toast.error('Failed to load mix. Please try again later.');
        } finally {
            loadingRef.current = false;
            setLoadingMixId(null);
            window.dispatchEvent(new CustomEvent('djpal-track-extract-end'));
        }
    }, [dispatch, toast]);

    const queueMix = useCallback(async (mix) => {
        const trackKey = mixTrackingKey(mix);
        if (!mix?.link) {
            toast.error('No audio source available for this mix');
            return;
        }
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoadingMixId(trackKey);
        try {
            let effectiveLink = mix.link;
            let result = await window.electron.djpal.extractStreamUrl(effectiveLink);
            if (result.error) {
                console.warn('[MixPlayback] queue extract failed, attempting lazy recovery', { mixId: mix.id, error: result.error });
                const recovery = await resolveMixLinkLazy(mix, {
                    replaceLink: mix.link,
                    extractError: result.error,
                    timeoutMs: 12000,
                });
                if (recovery?.link && recovery.link !== mix.link) {
                    effectiveLink = recovery.link;
                    result = await window.electron.djpal.extractStreamUrl(effectiveLink);
                }
            }
            if (result.error) {
                console.error('[MixPlayback] Queue error:', result.error);
                toast.error('Unable to add mix to queue. The audio source may be unavailable.');
                loadingRef.current = false;
                setLoadingMixId(null);
                return;
            }
            if (!result.streamUrl) {
                toast.error('No playable audio stream found for this mix');
                loadingRef.current = false;
                setLoadingMixId(null);
                return;
            }
            const cover = result.thumbnail || mix.artwork || mix.artworkUrl || mix.image || '';
            const name = mix.date
                ? `${mix.title || 'Untitled Mix'} (${mix.date})`
                : (mix.title || 'Untitled Mix');
            const isHls = !!result.isHls;
            const musicSrc = isHls ? silentUrlFor(result.streamUrl) : result.streamUrl;
            dispatch(addTrack({
                name,
                singer: mix.djName || mix.artist || '',
                cover,
                musicSrc,
                originalUrl: effectiveLink,
                isMix: true,
                isHls,
                ...(isHls ? { hlsSrc: result.streamUrl } : {}),
                mixId: entityId(mix),
                mixTitle: mix.title || '',
            }));
            toast.success('Added to Next Up');
        } catch (err) {
            console.error('[MixPlayback] Queue error:', err);
            toast.error('Failed to add mix to queue. Please try again.');
        } finally {
            loadingRef.current = false;
            setLoadingMixId(null);
        }
    }, [dispatch, toast]);

    return { playMix, queueMix, loadingMixId, playingMixId };
};
